/**
 * Where the API lives.
 *
 * Empty in dev, so paths stay relative and Vite's proxy handles them on one
 * origin. In production it is https://api.<domain>, set at BUILD time via
 * VITE_API_BASE_URL — Vite inlines env vars, so this is baked into the bundle
 * and changing it means rebuilding, not restarting.
 */
import type { ProviderId } from '@resolve/shared';

export const API_BASE = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');
const BASE = API_BASE;

/**
 * Requests send cookies. `include` rather than `same-origin` because the
 * dashboard and the API are different origins in production (apex vs api.*).
 * They are still the same SITE, so the SameSite=Lax session cookie is sent —
 * a genuinely different domain would have needed SameSite=None.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Supplies a Clerk session token, when Clerk is in use.
 *
 * Set from inside the provider tree, because the token comes from a hook and
 * this module is plain. Null when running on the password login, where the
 * session cookie does the work instead.
 */
let authTokenGetter: (() => Promise<string | null>) | null = null;

export function setAuthTokenGetter(fn: (() => Promise<string | null>) | null): void {
  authTokenGetter = fn;
}

/**
 * The impersonation token, when one is running.
 *
 * A header rather than a cookie because the dashboard and the API are
 * different origins, and sessionStorage rather than localStorage so the
 * borrowed access dies with the tab — which is the right lifetime for the most
 * dangerous mode on the platform.
 */
const IMPERSONATION_KEY = 'raios_impersonation';

export function impersonationToken(): string | null {
  try {
    return sessionStorage.getItem(IMPERSONATION_KEY);
  } catch {
    // Private windows and blocked site data both throw. Not impersonating is
    // always the safe reading.
    return null;
  }
}

export function setImpersonationToken(token: string | null): void {
  try {
    if (token) sessionStorage.setItem(IMPERSONATION_KEY, token);
    else sessionStorage.removeItem(IMPERSONATION_KEY);
  } catch {
    /* nothing to do: the request simply goes un-impersonated */
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = authTokenGetter ? await authTokenGetter().catch(() => null) : null;
  const impersonating = impersonationToken();
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    credentials: 'include',
    headers: {
      'content-type': 'application/json',
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      ...(impersonating ? { 'x-impersonate': impersonating } : {}),
      ...init.headers,
    },
  });

  const text = await res.text();
  let body: unknown;
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      body = text;
    }
  }

  if (!res.ok) {
    const message =
      (body as { error?: string } | undefined)?.error ?? `request failed (${res.status})`;
    throw new ApiError(res.status, message, body);
  }
  return body as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  put: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  del: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};

export interface DealerRow {
  id: string;
  provider: ProviderId;
  orgName: string;
  status: string;
  environment: string;
  activationDate?: string;
  configComplete: boolean;
  missingConfig: string[];
  watermarkAgeDays: number;
  degraded: boolean;
}

export interface DealerDetail {
  dealer: {
    _id: string;
    provider: ProviderId;
    orgName: string;
    status: string;
    environment: string;
    dealerCodes: string[];
    departmentIds: string[];
    config: Record<string, string | undefined>;
  };
  watermark: { sinceUtc: string; lastRunAt: string; consecutiveFailures: number } | null;
  watermarkAgeDays: number;
  missingConfig: string[];
}

export interface SourcePair {
  source: string;
  upType: string;
  count: number;
}

export interface AuditRow {
  requestId?: string;
  dealerId?: string;
  method?: string;
  url?: string;
  status?: number;
  ms?: number;
  createdAt?: string;
  /** Present only when one call is fetched by requestId. Token redacted. */
  requestHeaders?: Record<string, string>;
  requestBody?: unknown;
  responseHeaders?: Record<string, string>;
  responseBody?: unknown;
  truncated?: boolean;
}

export interface AdminUser {
  email: string;
  createdAt: string;
  lastLoginAt: string | null;
}

export interface Stats {
  dealers: { active: number; degraded: number; disabled: number };
  leads: { total: number; unsynced: number };
  calls24h: { total: number; failed: number };
}

export interface SettingsView {
  fortellisEnvironment: 'prod' | 'test' | 'dev';
  updatedAt: string | null;
  updatedBy: string | null;
  pathPrefix: string;
  pinnedByEnvVar: boolean;
  pinnedPathPrefix?: string;
  fallbackEnvironment: 'prod' | 'test' | 'dev';
  mismatchedDealers: number;
}

export interface ApiKeyRow {
  keyId: string;
  masked: string;
  name: string;
  ownerEmail: string;
  scopes: string[];
  dealerScope: 'all' | string[];
  createdAt: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
  expiresAt?: string | null;
  rotatedToKeyId?: string | null;
  rotatedFromKeyId?: string | null;
}

export interface ScopeInfo {
  scope: string;
  description: string;
  customerFacing: boolean;
}

export interface ActivityRow {
  at: string;
  actor: string;
  action: string;
  target?: string;
  detail?: Record<string, unknown>;
  ip?: string;
  outcome: 'ok' | 'denied' | 'failed';
}

export interface AuthConfig {
  provider: 'clerk' | 'password';
  publishableKey: string | null;
  registrationOpen: boolean;
}

export interface EmailProviderRow {
  id: string;
  kind: string;
  name: string;
  fromEmail: string;
  fromName?: string;
  active: boolean;
  config: Record<string, string>;
  secretsSet: string[];
  createdAt: string;
  lastUsedAt: string | null;
  lastError: string | null;
}

export interface EmailProviderKindInfo {
  kind: string;
  label: string;
  sendable: boolean;
  fields: {
    name: string;
    label: string;
    secret: boolean;
    required: boolean;
    placeholder?: string;
    help?: string;
  }[];
}

/** Permissions the signed-in user holds, from /admin/me. */
export interface Me {
  email: string;
  role: string;
  isDeveloper: boolean;
  authProvider?: string;
  permissions?: string[];
  /** 'dealer' accounts belong in /portal, not the console. */
  kind?: 'platform' | 'dealer';
  dealerScope?: 'all' | string[];
  /**
   * The address whose access is being borrowed, or null.
   *
   * `email` above stays the real person's throughout — impersonation swaps
   * what you can reach, never who the audit log says you are.
   */
  impersonating?: string | null;
}
